import { SelectChangeEvent } from '@mui/material'
import { useSnackbar } from 'notistack'
import React, { FC, useState } from 'react'
import { MaterialPropsType } from '../../api/appAPI'
import { EnqueueSnackbarType, showMessage } from '../common/Helpers'
import MaterialUIBox from './MaterialUIBox'

interface IProps {
  textures: string[]
  distributorCompanies: string[]

  addMaterial(
    materialProps: MaterialPropsType,
    enqueueSnackbar: EnqueueSnackbarType
  ): void
}

const DefineMaterial: FC<IProps> = (props) => {
  const [name, setName] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [sku, setSku] = useState('')
  const [type, setType] = useState('')
  const [cabinetDoor, setCabinetDoor] = useState('00000')
  const [thumbnailUrl, setThumbnailUrl] = useState('')
  const [textureUrl, setTextureUrl] = useState('')
  const [isTexture, setIsTexture] = useState('0')
  const [length, setLength] = useState(0)
  const [width, setWidth] = useState(0)
  const [description, setDescription] = useState('')
  const [weight, setWeight] = useState(0)
  const [price, setPrice] = useState('')
  const [addon, setAddon] = useState('')
  const [permissions, setPermissions] = useState('000')
  const [distributorCompany, setDistributorCompany] = useState('')
  const [threeProps, setThreeProps] = useState('')

  const { enqueueSnackbar } = useSnackbar()

  const handleNameChange = (event: React.ChangeEvent<{ value: string }>) => {
    setName(event.target.value.replace(/['"]/, ''))
  }

  const handleDisplayNameChange = (
    event: React.ChangeEvent<{ value: string }>
  ) => {
    setDisplayName(event.target.value.replace(/['"]/, ''))
  }

  const handleSkuChange = (event: React.ChangeEvent<{ value: string }>) => {
    setSku(event.target.value.replace(/['"]/, ''))
  }

  const handleTypeChange = (event: SelectChangeEvent<string>) => {
    setType(event.target.value)
  }

  // const handleThumbnailUrl = (event: React.ChangeEvent<{ value: unknown }>) => {
  //     setThumbnailUrl(event.target.value as string)
  // }

  // const handleTextureUrl = (event: React.ChangeEvent<{ value: unknown }>) => {
  //     setTextureUrl(event.target.value as string)
  // }

  const handleLengthChange = (value: number) => {
    setLength(value)
  }

  const handleWidthChange = (value: number) => {
    setWidth(value)
  }

  const handleDescriptionChange = (
    event: React.ChangeEvent<{ value: string }>
  ) => {
    setDescription(event.target.value.replace(/['"]/, ''))
  }

  const handleWeightChange = (value: number) => {
    setWeight(value)
  }

  const handlePriceChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setPrice(event.target.value as string)
  }

  const handleAddonChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setAddon(event.target.value as string)
  }

  const handleDistributorCompanyChange = (event: SelectChangeEvent<string>) => {
    setDistributorCompany(event.target.value)
  }

  const handleThreePropsChange = (
    event: React.ChangeEvent<{ value: string }>
  ) => {
    if (event.target.value.includes("'")) return

    setThreeProps(encodeURI(event.target.value))
  }

  const handleCreateClick = () => {
    if (name === '') {
      showMessage(enqueueSnackbar, 'Enter material name')
      return
    }

    // if (sku === '') {
    //     showMessage(enqueueSnackbar, "Enter material sku")
    //     return
    // }

    if (type === '') {
      showMessage(enqueueSnackbar, 'Select material type')
      return
    }

    if (length === 0) {
      showMessage(enqueueSnackbar, 'Enter material texture length')
      return
    }

    if (width === 0) {
      showMessage(enqueueSnackbar, 'Enter material texture width')
      return
    }

    if (price === '') {
      showMessage(enqueueSnackbar, 'Enter material price')
      return
    }

    if (addon === '') {
      showMessage(enqueueSnackbar, 'Enter material price addon')
      return
    }

    const materialProps = {
      name,
      displayName,
      sku,
      type,
      cabinetDoor,
      thumbnail: thumbnailUrl,
      texture: textureUrl,
      length,
      width,
      description,
      weight,
      price: parseFloat(price),
      addon: parseFloat(addon),
      permissions,
      editable: '1',
      distributorCompany,
      threeProps: encodeURI(threeProps),
    } as MaterialPropsType

    props.addMaterial(materialProps, enqueueSnackbar)

    setName('')
    setDisplayName('')
    setSku('')
    setType('')
    setCabinetDoor('00000')
    setThumbnailUrl('')
    setTextureUrl('')
    setIsTexture('0')
    setLength(0)
    setWidth(0)
    setDescription('')
    setWeight(0)
    setPrice('')
    setAddon('')
    setPermissions('000')
    setDistributorCompany('')
    setThreeProps('')
  }

  return (
    <MaterialUIBox
      title="New Material"
      name={{
        value: name,
        onChange: handleNameChange,
      }}
      displayName={{
        value: displayName,
        onChange: handleDisplayNameChange,
      }}
      sku={{
        value: sku,
        onChange: handleSkuChange,
      }}
      type={{
        value: type,
        onChange: handleTypeChange,
      }}
      cabinetDoor={{
        value: cabinetDoor,
        set: setCabinetDoor,
      }}
      thumbnailUrl={{
        value: thumbnailUrl,
        onChange: setThumbnailUrl,
      }}
      textureUrl={{
        value: textureUrl,
        onChange: (value: string) => {
          setTextureUrl(value)
          setThumbnailUrl(import.meta.env.VITE_TEXTURE_FOLDER_URL + value)
        },
      }}
      isTexture={{
        value: isTexture,
        set: setIsTexture,
      }}
      length={{
        value: length,
        onChange: handleLengthChange,
      }}
      width={{
        value: width,
        onChange: handleWidthChange,
      }}
      description={{
        value: description,
        onChange: handleDescriptionChange,
      }}
      weight={{
        value: weight,
        onChange: handleWeightChange,
      }}
      price={{
        value: price,
        onChange: handlePriceChange,
      }}
      addon={{
        value: addon,
        onChange: handleAddonChange,
      }}
      permissions={{
        value: permissions,
        set: setPermissions,
      }}
      distributorCompany={{
        value: distributorCompany,
        onChange: handleDistributorCompanyChange,
      }}
      distributorCompanies={props.distributorCompanies}
      threeProps={{
        value: threeProps,
        onChange: handleThreePropsChange,
      }}
      button={{
        text: 'Create',
        onClick: handleCreateClick,
      }}
      textures={props.textures}
      editable
    />
  )
}

export default DefineMaterial
